/**
 * X18, step 2: the installations' jobs act (ARCHITECTURE §5.13 "X18 on the table"). Behind `GameConfig.installations`
 * (off by default: nothing here changes a result); on, a team that holds an installation — a standing structure it
 * owns, or a ruin it has claimed — gets its job:
 *
 * - **shield** (the shield structure): a blast on a teammate within its reach on its planet is scaled by its factor;
 * - **comms** (the antenna structure, a claimed comms relay): the team picture is on for that team — every bot fires
 *   on and walks to the team's tracks, as GameConfig.teamPicture gives it to everyone;
 * - **forecast** (a claimed weather station): the forecast beyond the present weather is seen on that planet; without
 *   one, only the weather now;
 * - **landing** (the landing pad structure, a claimed landing-pad ruin): the team's reserves land at it, not at the
 *   lane they came by (core/reserves.ts).
 *
 * Pure; the dials are INSTALLATIONS (constants/body.ts), unplayed guesses.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import type { StructureJob, RuinJob } from '../types/body';
import type { WeatherKind } from './weather';
import type { ExitRegion } from './withdrawal';
import { INSTALLATIONS } from '../constants/body';
import { structureJob, ruinJob } from './body';
import { arcDistance } from './gameState';
import { planetForecast, planetWeather } from './weather';

type Held = Pick<GameState, 'config' | 'structures' | 'ruins' | 'players'>;
type HeldJob = keyof StructureJob & keyof RuinJob;

/** Is X18's second step on in this match? */
export function installationsOn(state: Pick<GameState, 'config'>): boolean {
  return !!state.config.installations;
}

/** The team an owned structure serves: its owner's; null for nobody's. */
function structureTeam(state: Pick<GameState, 'players'>, ownerId: number): number | null {
  return state.players.find(p => p.id === ownerId)?.teamIndex ?? null;
}

/** Where a team's installations doing `job` stand — its standing structures and its claimed ruins (on `planetIndex` when given). */
export function heldInstallations(state: Held, team: number, job: HeldJob, planetIndex?: number): Array<{ planetIndex: number; surfaceDir: Vec3; id: number; kind: 'structure' | 'ruin' }> {
  if (!installationsOn(state)) return [];
  const out: Array<{ planetIndex: number; surfaceDir: Vec3; id: number; kind: 'structure' | 'ruin' }> = [];
  for (const s of state.structures) {
    if (s.health <= 0 || structureJob(s)[job] === undefined || structureTeam(state, s.ownerId) !== team) continue;
    if (planetIndex !== undefined && s.planetIndex !== planetIndex) continue;
    out.push({ planetIndex: s.planetIndex, surfaceDir: s.surfaceDir, id: s.id, kind: 'structure' });
  }
  for (const r of state.ruins ?? []) {
    if (!r.claimBenefitActive || r.claimTeamIndex !== team || ruinJob(r)[job] === undefined) continue;
    if (planetIndex !== undefined && r.planetIndex !== planetIndex) continue;
    out.push({ planetIndex: r.planetIndex, surfaceDir: r.surfaceDir, id: r.id, kind: 'ruin' });
  }
  return out;
}

/** Does the team hold an installation doing `job` (on `planetIndex` when given)? */
export function teamHoldsInstallation(state: Held, team: number, job: HeldJob, planetIndex?: number): boolean {
  return heldInstallations(state, team, job, planetIndex).length > 0;
}

/** comms: the team picture is on for this team through an installation (GameConfig.teamPicture is the other way). */
export function commsPictureOn(state: Held, team: number): boolean {
  if (!installationsOn(state)) return false;
  // 62.57 cut 5: every bot asked every tick — the answer kept while the structures, ruins, players and config are the
  // very objects they were (any change makes a new one), per team
  const m = commsMemo;
  if (m && m.structures === state.structures && m.ruins === state.ruins && m.players === state.players && m.config === state.config) {
    const hit = m.byTeam.get(team);
    if (hit !== undefined) return hit;
    const v = teamHoldsInstallation(state, team, 'comms');
    m.byTeam.set(team, v);
    return v;
  }
  const v = teamHoldsInstallation(state, team, 'comms');
  commsMemo = { structures: state.structures, ruins: state.ruins, players: state.players, config: state.config, byTeam: new Map([[team, v]]) };
  return v;
}
let commsMemo: { structures: unknown; ruins: unknown; players: unknown; config: unknown; byTeam: Map<number, boolean> } | null = null;

/**
 * shield: what a blast on `unit` is scaled by — the shield's factor when a standing shield of its team on its planet
 * is within reach of where it stands, else 1. The blast's own caller multiplies only when this is not 1.
 */
export function shieldFactorFor(state: Pick<GameState, 'config' | 'structures' | 'players' | 'planets'>, unit: Pick<PlayerState, 'teamIndex' | 'planetIndex' | 'surfaceDir'>): number {
  if (!installationsOn(state)) return 1;
  const planet = state.planets[unit.planetIndex];
  if (!planet) return 1;
  for (const s of state.structures) {
    const shield = structureJob(s).shield;
    if (!shield || s.health <= 0 || s.planetIndex !== unit.planetIndex || structureTeam(state, s.ownerId) !== unit.teamIndex) continue;
    if (arcDistance(s.surfaceDir, unit.surfaceDir, planet.radius) <= shield.reach) return shield.factor;
  }
  return 1;
}

/** landing: the region a team's reserves land in — its newest held landing installation, facing it; null = the lane it came by. */
export function landingRegionFor(state: Held, team: number): ExitRegion | null {
  const held = heldInstallations(state, team, 'landing');
  if (!held.length) return null;
  const pad = held[held.length - 1];
  return { planetIndex: pad.planetIndex, facing: pad.surfaceDir, halfAngle: INSTALLATIONS.landingHalfAngle };
}

/**
 * forecast: what a team sees of a planet's weather — the whole forecast when it holds a weather station there (or the
 * match has no installations: today's rule), else the present weather alone. Null when the match has no weather.
 */
export function forecastSeenBy(state: Held & Pick<GameState, 'turnNumber' | 'planets'>, team: number | null, planetIndex: number, count: number, actionSeconds = 0): WeatherKind[] | null {
  if (!installationsOn(state) || team === null || teamHoldsInstallation(state, team, 'forecast', planetIndex)) return planetForecast(state, planetIndex, count, actionSeconds);
  const now = planetWeather(state, planetIndex, actionSeconds);
  return now === null ? null : [now];
}
